import { useI18n } from "@core/i18n/i18n";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { sendAnalyticsEventViewGuide } from "@domain/program/program-analytics";
import styled from "@emotion/styled";
import { TippyProps } from "@tippyjs/react";
import { WordingKey } from "@typings/i18n-typings";
import { OnboardingPanel } from "@ui/components/onboarding/onboarding-panel";
import { Tooltip } from "@ui/components/common/tooltip";
import { colors } from "@ui/style/colors";

import React, { useEffect } from "react";

interface OnboardingTooltipProps extends TippyProps {
	onboardingStep: OnboardingStepsWithTooltip;
	onClose?: (e: React.MouseEvent<SVGElement, MouseEvent>) => void;
}

type OnboardingStepsWithTooltip =
	| OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_01
	| OnboardingProgramQuickstartStep.START_TEMPLATE_GUIDE_01
	| OnboardingProgramQuickstartStep.START_DRAFT_GUIDE_01
	| OnboardingProgramQuickstartStep.START_GUIDE_02;

const ONBOARDING_TOOLTIP_WORDINGS = {
	[OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_01]:
		"programBuilder.quickstart.step.complementaryExercises.guide01",
	[OnboardingProgramQuickstartStep.START_TEMPLATE_GUIDE_01]:
		"programBuilder.quickstart.step.startTemplate.guide01",
	[OnboardingProgramQuickstartStep.START_DRAFT_GUIDE_01]:
		"programBuilder.quickstart.step.startDraft.guide01",
	[OnboardingProgramQuickstartStep.START_GUIDE_02]:
		"programBuilder.quickstart.step.startGuide02",
} satisfies Record<OnboardingStepsWithTooltip, WordingKey>;

export const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({ onboardingStep, onClose, visible, ...props }) => {
	const { format } = useI18n();

	useEffect(() => {
		if (visible) {
			sendAnalyticsEventViewGuide(onboardingStep);
		}
	}, [onboardingStep, visible]);

	return (
		<StyledTooltip
			interactive
			appendTo={() => document.body}
			zIndex={10000}
			popperOptions={{ strategy: "fixed" }}
			content={
				<OnboardingPanel onClose={e => onClose?.(e)} text={format(ONBOARDING_TOOLTIP_WORDINGS[onboardingStep])} />
			}
			visible={visible}
			{...props}
		/>
	);
};

const StyledTooltip = styled(Tooltip)`
	padding: 0;

	.tippy-arrow {
		color: ${colors.onboardingPrimary};
	}

	.tippy-content {
		padding: 0;
	}
`;
